import { Point } from '@arcgis/core/geometry.js';
import { locationToAddress } from '@arcgis/core/rest/locator';
import * as Sentry from '@sentry/react';
import {
  AssetConfig,
  AssetType,
  AssetTypeConfig,
  assetTypeId,
  CycleHangarsEstatesFeatureAttributes,
  CycleHangarsFeatureAttributes,
  HighwaysCarriagewaysFeatureAttributes,
  NearestApiResponse,
} from '@types';
import axios from 'axios';
import * as ELG from 'esri-leaflet';
import L, {
  Icon,
  LatLngTuple,
  LayerGroup,
  LeafletMouseEvent,
  Map,
} from 'leaflet';
import proj4 from 'proj4';
import {
  WCC_ARCGIS_MAP_SERVER_BASE_URL,
  GEOCODE_SERVER,
  LOCATION_ENDPOINT,
  apiKey,
  NEAREST_ENDPOINT,
  NEAREST_ASSET_ENDPOINT,
  WITHIN_BOUNDARY_ENDPOINT,
} from './Map/constants';
import { createAssetIcon } from './Map/icons';
import { boroughBoundaryLayer } from './Map/layers';
import {
  tflBoundaryPopup,
  loadingPopup,
  errorZoomInPopup,
  outsideBoroughPopup,
  addressErrorPopup,
} from './Map/mapPopups';
import {
  containsAddressFilterItem,
  isLatLngWithinBoundary,
  isMapCorrectZoomLevel,
  isWithinTfLBoundary,
} from './Map/validations';
import extendedBoundaryGeojson from './Map/WCC_EXTENDED_BOUNDARY.json';

export const queryFeatureLayerById = async (
  assetLayerType: AssetType,
  assetLabel: string,
  assetId: string,
) => {
  const queryParams = {
    where: `${assetLabel}='${assetId}'`,
    timeRelation: 'esriTimeRelationOverlaps',
    inSR: '4326',
    spatialRel: 'esriSpatialRelIntersects',
    distance: '4',
    units: 'esriSRUnit_Foot',
    outFields: '*',
    returnGeometry: 'true',
    returnTrueCurves: 'false',
    returnIdsOnly: 'false',
    returnCountOnly: 'false',
    returnZ: 'false',
    returnM: 'false',
    returnDistinctValues: 'false',
    sqlFormat: 'none',
    featureEncoding: 'esriDefault',
    f: 'geojson',
  };

  const queryString = Object.entries(queryParams)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');

  const queryUrl = `${WCC_ARCGIS_MAP_SERVER_BASE_URL}/${assetTypeId[assetLayerType]}/query?${queryString}`;

  try {
    const response = await axios.get(queryUrl);
    return response;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

export const createAssetFeatureLayer = (assetConfig: AssetTypeConfig) => {
  const assetType =
    typeof assetConfig === 'string' ? assetConfig : assetConfig.asset;
  const feature = `${WCC_ARCGIS_MAP_SERVER_BASE_URL}${assetTypeId[assetType]}`;

  return ELG.featureLayer({
    url: feature,
    simplifyFactor: 0.5,
    precision: 5,
    style: (feature) => {
      if (typeof assetConfig === 'string') {
        return { color: '#5897fcad', weight: 2 };
      }
      return {
        color: assetConfig.color || '#5897fcad',
        weight: 2,
        opacity: assetConfig.opacity || 1,
        stroke: true,
        strokeColor: assetConfig.strokeColor,
      };
    },
    pointToLayer: (geojson, latlng) => {
      if (typeof assetConfig === 'string') {
        return L.marker(latlng, { icon: createAssetIcon() });
      }

      // If a custom Icon object is provided, use it directly
      if (assetConfig.icon instanceof Icon) {
        return L.marker(latlng, { icon: assetConfig.icon });
      }

      // Otherwise use our custom icon configuration
      return L.marker(latlng, {
        icon: createAssetIcon({
          shape: assetConfig.icon?.shape,
          size: assetConfig.icon?.size,
          fillColor: assetConfig.color,
          strokeColor: assetConfig.strokeColor,
          opacity: assetConfig.opacity,
        }),
      });
    },
  });
};

export const dropMarkerOnMap = (
  event: LeafletMouseEvent,
  address: addressResponseType,
  mapMethods: MapMethods,
) => {
  const { addMarker, clearGraphics, map } = mapMethods;
  clearGraphics();
  const { lat, lng } = event.latlng;
  const popupContent = `<div padding: 10px; border-radius: 5px;">
            <span>${address.full_address}</span></div>`;
  map.flyTo([lat, lng]);
  addMarker([lat, lng]);
};

export function shimmer() {
  // create shimmer container
  const container = document.createElement('div');
  const child = document.createElement('div');
  container.appendChild(child);
  // create stylesheet
  const styleEl = document.createElement('style');
  document.head.appendChild(styleEl);
  const styleSheet = styleEl.sheet as CSSStyleSheet;
  const keyFrames = `@keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }`;
  styleSheet.insertRule(keyFrames, styleSheet.cssRules.length);
  container.style.cssText = `
    width: 100%;
    height: 38px;
    border-radius:5px;
    background-color: #ddd;
    position:relative;
    overflow:hidden;
  `;
  child.style.cssText = `
    width: 100%;
    height: 100%;
    background: linear-gradient(to right, #ddd 0%, #f5f5f5 50%, #ddd 100%);
    background-repeat: no-repeat;
    background-size: 200% 100%;
    animation: loading 1.0s infinite;
    position:absolute;
  `;
  return container;
}

export const getAddressFromMapPoint = async (
  point: LatLngTuple,
): Promise<__esri.AddressCandidate | null> => {
  let address = null;
  const coords = new Point({ latitude: point[0], longitude: point[1] });
  await locationToAddress(
    GEOCODE_SERVER,
    {
      location: coords,
      // @ts-expect-error "wkid" is the only required property in the spatialReference object to output the correct coordinates
      outSpatialReference: { wkid: 4326 },
    },
    500,
  )
    .then((response) => {
      address = response;
    })
    .catch((e) => {
      address = null;
    });
  return address;
};

export type addressResponseType = {
  address_line_1: string;
  address_line_2: string;
  town: string;
  county: string;
  postcode: string;
  country: string;
  UPRN: string;
  USRN: string;
  latitude: string;
  longitude: string;
  full_address: string;
  asset?:
    | HighwaysCarriagewaysFeatureAttributes
    | CycleHangarsEstatesFeatureAttributes
    | CycleHangarsFeatureAttributes;
  easting?: string;
  northing?: string;
};

interface GetResultsHandler {
  suggestTerm: string;
  sourceIndex: number;
}

export const handleGetSuggestions = async (
  params: GetResultsHandler,
  custodianCodes: CUSTODIAN_CODES[] = [CUSTODIAN_CODES.CITY_OF_WESTMINSTER],
) => {
  const requestUrl = `${LOCATION_ENDPOINT}?query=${encodeURIComponent(
    params.suggestTerm,
  )}&fq=${generateCustodianCodeList(custodianCodes)}&custom_sort=S`;
  return await axios
    .get(requestUrl, { headers: { 'Ocp-Apim-Subscription-Key': apiKey } })
    .then((response) => {
      if (
        response.data.results === undefined ||
        response.data.results.length === 0
      ) {
        console.warn('No suggestions found');
        return [];
      }
      return response.data.results
        .filter((suggestion: addressResponseType) => suggestion !== null)
        .filter(
          (suggestion: addressResponseType) =>
            !containsAddressFilterItem(suggestion.address_line_1),
        )
        .map((suggestion: addressResponseType) =>
          removeStreetRecordLabelFromResults(suggestion),
        );
    })
    .catch((error) => {
      Sentry.captureException(error);
      return [];
    });
};

export enum CUSTODIAN_CODES {
  CITY_OF_WESTMINSTER = '5990',
  CAMDEN = '5210',
  BRENT = '5150',
  KENSINGTON_AND_CHELSEA = '5600',
  CITY_OF_LONDON = '5030',
  LAMBETH = '5660',
  WANDSWORTH = '5960',
  ISLINGTON = '5570',
  SOUTHWARK = '5840',
  HAMMERSMITH_AND_FULHAM = '5390',
}

/**
 * Generate a list of custodian codes separated by a space, ie: LOCAL_CUSTODIAN_CODE:5990 LOCAL_CUSTODIAN_CODE:5210
 * @param custodianCodeArray
 * @returns list of custodian codes
 */
export const generateCustodianCodeList = (
  custodianCodeArray: CUSTODIAN_CODES[],
) => {
  return custodianCodeArray
    .map((code) => `LOCAL_CUSTODIAN_CODE:${code}`)
    .join(' ');
};

export const getNearestAddress = async (
  latitude: number,
  longitude: number,
  custodianCodes: CUSTODIAN_CODES[] = [CUSTODIAN_CODES.CITY_OF_WESTMINSTER],
): Promise<addressResponseType | null> => {
  try {
    const requestUrl = `${NEAREST_ENDPOINT}?point=${encodeURIComponent(
      `${latitude},${longitude}`,
    )}&fq=${generateCustodianCodeList(custodianCodes)}`;
    const response = await axios.get(requestUrl, {
      headers: { 'Ocp-Apim-Subscription-Key': apiKey },
    });
    if (response.data.results && response.data.results.length > 0) {
      // Projections to get eastings and northings
      const wgs84 = '+proj=longlat +datum=WGS84 +no_defs';
      const osgb36 =
        '+proj=tmerc +lat_0=49 +lon_0=-2 +k=0.9996012717 +x_0=400000 +y_0=-100000 +datum=OSGB36 +units=m +no_defs';
      const [easting, northing] = proj4(wgs84, osgb36, [longitude, latitude]);

      //APIM doesn't accept floats, only the first half is required
      const eastingString = easting.toString().split('.')[0];
      const northingString = northing.toString().split('.')[0];
      const latitudeString = latitude.toString();
      const longitudeString = longitude.toString();

      const result = removeStreetRecordLabelFromResults(
        response.data.results[0],
      );
      return {
        ...result,
        latitude: latitudeString,
        longitude: longitudeString,
        easting: eastingString,
        northing: northingString,
      };
    } else {
      return null;
    }
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

export const getNearestAsset = async (
  latitude: number,
  longitude: number,
  assetType: AssetType,
  tflAssetParam?: boolean,
): Promise<NearestApiResponse | null> => {
  try {
    const requestUrl = `${NEAREST_ASSET_ENDPOINT}?latitude=${latitude}&longitude=${longitude}&layer=${assetType}&includeTFLAsset=${!!tflAssetParam}`;
    const response = await axios.get(requestUrl, {
      headers: { 'Ocp-Apim-Subscription-Key': apiKey },
    });
    if (response.data) {
      return response.data;
    } else {
      return null;
    }
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

export const getWithinBoundaryCheck = async (
  latitude: number,
  longitude: number,
) => {
  try {
    const requestUrl = `${WITHIN_BOUNDARY_ENDPOINT}?latitude=${latitude}&longitude=${longitude}`;
    const response = await axios.get(requestUrl, {
      headers: { 'Ocp-Apim-Subscription-Key': apiKey },
    });
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

export interface MapMethods {
  clearGraphics: () => void;
  addMarker: (latlng: LatLngTuple, popupContent?: string | undefined) => void;
  onChange: (location: addressResponseType) => void;
  map: Map;
  assetType?: AssetTypeConfig[] | AssetTypeConfig | undefined;
  graphicsLayer: React.MutableRefObject<LayerGroup<any> | null>;
}

export interface MapMethodProps {
  mapMethods: MapMethods;
  searchTerm: string;
  setSearchTerm: (searchTerm: string) => void;
  assetType?: AssetTypeConfig[] | undefined;
  enableBuffer?: boolean;
  enableTfLBoundaryCheck?: boolean;
}

export const addAsset = async (
  assetType: AssetTypeConfig | AssetTypeConfig[] | undefined,
  onChange: (location: addressResponseType) => void,
  address: addressResponseType,
  feature?: { properties: HighwaysCarriagewaysFeatureAttributes } | null,
) => {
  let assetAttributes: HighwaysCarriagewaysFeatureAttributes | undefined;
  if (feature?.properties) {
    // Use the feature properties directly if provided
    assetAttributes = feature.properties;
  } else if (assetType !== undefined) {
    // Fall back to API call if no feature provided
    const assetString =
      typeof (assetType as AssetType)[0] === 'string'
        ? assetType
        : (assetType as AssetConfig[])[0].asset;
    const asset = await getNearestAsset(
      Number.parseFloat(address.latitude),
      Number.parseFloat(address.longitude),
      assetString as AssetType,
    );
    if (asset?.features) {
      const attributes = asset.features
        .filter((feature) => feature.attributes)
        .map((feature) => feature.attributes);
      assetAttributes = attributes[0];
    }
  }
  if (assetAttributes && assetType) {
    onChange({
      ...address,
      asset: assetAttributes,
    });
  } else {
    onChange(address);
  }
};

export const getCoordinates = (enableBuffer?: boolean) => {
  if (enableBuffer) {
    return extendedBoundaryGeojson.features[0].geometry.coordinates;
  }
  let coordinates: number[][][] | undefined = undefined;
  boroughBoundaryLayer.eachFeature((feat) => {
    if ('feature' in feat) {
      coordinates = feat['feature'].geometry.coordinates as number[][][];
    }
  });
  return coordinates;
};

export const checkTfLBoundaryAndDisplayPopup = async (
  assetType: AssetTypeConfig | undefined,
  address: addressResponseType,
  event: LeafletMouseEvent,
  map: Map,
) => {
  if (assetType) {
    const isInsideTfLBoundary = await isWithinTfLBoundary(address, assetType);
    if (isInsideTfLBoundary) {
      tflBoundaryPopup(event, map);
      return;
    }
  }
};

export const onMapClick = async (
  event: LeafletMouseEvent,
  mapMethods: MapMethods,
  setSearchTerm?: (searchTerm: string) => void,
  enableBuffer?: boolean,
) => {
  const { map, onChange, assetType, clearGraphics } = mapMethods;
  const { lat, lng } = event.latlng;
  setSearchTerm && setSearchTerm('');
  clearGraphics();
  loadingPopup(event, map);

  const correctZoomLevel = isMapCorrectZoomLevel(map);
  if (!correctZoomLevel) {
    errorZoomInPopup(event, map);
    return;
  }

  try {
    if (
      !isLatLngWithinBoundary(event.latlng.lat, event.latlng.lng, enableBuffer)
    ) {
      outsideBoroughPopup(event, map);
      return;
    }

    let addressFromPin = await getNearestAddress(
      lat,
      lng,
      enableBuffer ? Object.values(CUSTODIAN_CODES) : undefined,
    );
    if (addressFromPin === null) {
      addressErrorPopup(map, event);
      return;
    }

    // Check if the assetType array contains 'HighwaysCarriageways' or assetType is 'HighwaysCarriageways'
    const testTfLBoundaryCondition = Array.isArray(assetType)
      ? assetType.includes('HighwaysCarriageways')
      : assetType === 'HighwaysCarriageways';

    if (testTfLBoundaryCondition) {
      // Check if the asset is within TfL boundary
      const withinTfLBoundary = await isWithinTfLBoundary(
        addressFromPin,
        'HighwaysCarriageways',
      );

      if (withinTfLBoundary) {
        tflBoundaryPopup(event, map);
        return;
      }
    }

    await addAsset(assetType, onChange, addressFromPin);
    map.closePopup();
    setSearchTerm && setSearchTerm(addressFromPin.full_address);
    dropMarkerOnMap(event, addressFromPin, mapMethods);
  } catch (error) {
    Sentry.captureException(error);
    addressErrorPopup(map, event);
  }
};

export const removeStreetRecordLabelFromResults = (
  address: addressResponseType,
) => {
  if (address.address_line_1 === 'STREET RECORD') {
    address.full_address = address.full_address.replace('STREET RECORD, ', '');
  }

  return address;
};
